/**
 * Custom hook to get the current organization data for the user.
 *
 * @author Rammetec
 * @version 1.0.0
 * @since 2025-11-18
 */
import { useQuery } from '@tanstack/react-query';
import { useCurrentUser } from './useCurrentUser.js';
import { useSessionManager } from './useSessionManager.js';
import { organizationService } from '@syscolect/domain';
import { useDispatchHookEvent } from '@rammetec/core';
/**
 * Custom hook to get the current organization data.
 *
 * @returns {Object} - Object containing the current organization data, the loading state and error.
 *
 * Hook that fetches the currently selected organization data for the active session.
 */
export const useCurrentOrganizationData = () => {
    const { currentUserId } = useCurrentUser();
    const { currentOrganizationId, isLoading: isSessionLoading } = useSessionManager();
    // Query to get current organization data
    const { data: currentOrganizationData, isLoading: isOrgDataLoading, error: orgDataError } = useQuery({
        queryKey: ['org', currentOrganizationId],
        queryFn: () => {
            if (!currentOrganizationId)
                return null;
            return organizationService.getOrganizationData(currentOrganizationId);
        },
        enabled: !!currentOrganizationId,
    });
    const { data: hasValidOrgAvailable, isLoading: isHasValidOrgLoading } = useQuery({
        queryKey: ['hasValidOrgAvailable', currentUserId],
        queryFn: () => currentUserId ? organizationService.hasValidOrganizationAvailable(currentUserId) : false,
        enabled: !!currentUserId
    });
    const isLoading = isSessionLoading || isOrgDataLoading || isHasValidOrgLoading;
    useDispatchHookEvent('useCurrentOrganizationData', {
        currentOrganizationData,
        isLoading,
        error: orgDataError,
        hasValidOrgAvailable
    });
    return {
        currentOrganizationData: currentOrganizationData ?? undefined, // Match potential undefined type
        organizationData: currentOrganizationData ?? undefined, // Alias for backward compatibility if needed (DashboardAppBar uses organizationData)
        isLoading: isLoading, // Alias for backward compatibility
        isCurrentOrganizationDataLoading: isLoading,
        error: orgDataError,
        hasValidOrgAvailable: hasValidOrgAvailable ?? false
    };
};
//# sourceMappingURL=useCurrentOrganization.js.map